/**
 * Dual-auth HTTP client with CSRF protection and auto-refresh.
 *
 * Auth strategy:
 *   Inside Telegram Mini App  → Authorization: tma <initData>
 *   Browser (standalone web)  → session cookie (credentials: 'include')
 *
 * CSRF protection: every request carries X-Requested-With: XMLHttpRequest.
 * Browsers cannot set this header on cross-site simple requests, so its
 * presence proves the call originated from our own JS.
 *
 * Token refresh: on 401, tries POST /api/auth/refresh once. If that
 * succeeds the original request is retried transparently. If refresh
 * also fails a 'auth:logout' CustomEvent is dispatched so the app can
 * redirect to the login page.
 */

const BASE = '/api'
let _refreshing: Promise<boolean> | null = null

function getInitData(): string {
  try {
    return window.Telegram?.WebApp?.initData ?? ''
  } catch {
    return ''
  }
}

function buildHeaders(hasBody: boolean, isFormData: boolean): Record<string, string> {
  const headers: Record<string, string> = {
    'X-Requested-With': 'XMLHttpRequest',
  }
  const initData = getInitData()
  if (initData) {
    headers['Authorization'] = `tma ${initData}`
  }
  // FormData bodies must NOT get a manual Content-Type — the browser
  // sets its own with the multipart boundary, and overriding it here
  // would break the upload.
  if (hasBody && !isFormData) {
    headers['Content-Type'] = 'application/json'
  }
  return headers
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

async function tryRefresh(): Promise<boolean> {
  // Deduplicate concurrent refresh calls — only one in-flight at a time.
  if (_refreshing) return _refreshing
  _refreshing = (async () => {
    try {
      const resp = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      })
      return resp.ok
    } catch {
      return false
    } finally {
      _refreshing = null
    }
  })()
  return _refreshing
}

async function request<T>(path: string, options: RequestInit = {}, retry = true): Promise<T> {
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), 30_000)

  const hasBody = options.body !== undefined
  const isFormData = options.body instanceof FormData
  let resp: Response
  try {
    resp = await fetch(`${BASE}${path}`, {
      ...options,
      credentials: 'include',
      headers: { ...buildHeaders(hasBody, isFormData), ...options.headers },
      signal: controller.signal,
    })
  } catch (e) {
    clearTimeout(timeout)
    window.dispatchEvent(new CustomEvent('service:updating', { detail: { error: e } }))
    if ((e as Error).name === 'AbortError') throw new ApiError(0, 'Request timeout')
    throw e
  }
  clearTimeout(timeout)

  // Auto-refresh on 401 (only for cookie-auth, not tma — tma tokens don't
  // refresh, they need the mini-app reopened for Telegram to hand back a
  // fresh initData). Either way a genuine 401 means the caller is no
  // longer authenticated, so 'auth:logout' must fire for BOTH paths —
  // otherwise the global auth store keeps believing a stale tma session
  // is valid (set once at boot in checkAuth()) and LoginPage bounces the
  // user straight back before they can re-authenticate.
  if (resp.status === 401 && retry) {
    if (!getInitData()) {
      const refreshed = await tryRefresh()
      if (refreshed) {
        return request<T>(path, options, false)
      }
    }
    window.dispatchEvent(new CustomEvent('auth:logout'))
    throw new ApiError(401, 'session_expired')
  }

  if (!resp.ok) {
    if ([502, 503, 504].includes(resp.status)) {
      window.dispatchEvent(new CustomEvent('service:updating', { detail: { status: resp.status } }))
    }
    let detail = `HTTP ${resp.status}`
    try {
      const body = await resp.json()
      if (body?.detail) detail = String(body.detail)
      else if (body?.error) detail = String(body.error)
    } catch {
      // ignore
    }
    throw new ApiError(resp.status, detail)
  }

  return resp.json() as Promise<T>
}

async function requestBlob(path: string, options: RequestInit = {}, retry = true): Promise<Blob> {
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), 60_000)

  let resp: Response
  try {
    resp = await fetch(`${BASE}${path}`, {
      ...options,
      credentials: 'include',
      headers: { ...buildHeaders(false, false), ...options.headers },
      signal: controller.signal,
    })
  } catch (e) {
    clearTimeout(timeout)
    if ((e as Error).name === 'AbortError') throw new ApiError(0, 'Request timeout')
    throw e
  }
  clearTimeout(timeout)

  if (resp.status === 401 && retry) {
    if (!getInitData()) {
      const refreshed = await tryRefresh()
      if (refreshed) {
        return requestBlob(path, options, false)
      }
    }
    window.dispatchEvent(new CustomEvent('auth:logout'))
    throw new ApiError(401, 'session_expired')
  }

  if (!resp.ok) {
    let detail = `HTTP ${resp.status}`
    try {
      const body = await resp.json()
      if (body?.detail) detail = String(body.detail)
      else if (body?.error) detail = String(body.error)
    } catch {
      // ignore
    }
    throw new ApiError(resp.status, detail)
  }

  return resp.blob()
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  getBlob: (path: string) => requestBlob(path),
  post: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  postForm: <T>(path: string, formData: FormData) =>
    request<T>(path, { method: 'POST', body: formData }),
}
